import React from "react";
import { Link } from "react-router-dom";
import HUDFrame from "./HUDFrame";
import { Sprout, Truck, UtensilsCrossed, Boxes } from "lucide-react";

/**
 * "Platform Reach" section — positions StrainScan Pro's AI as a
 * platform whose vision + classification tech extends across broader
 * food and agricultural industries.
 *
 * Sits after the hero on the homepage so the user first internalizes
 * the core product, then discovers the platform reach.
 */

const INDUSTRIES = [
  {
    Icon: Sprout,
    title: "AGRICULTURAL\nQUALITY CONTROL",
    desc: "Detect crop disease, ripeness, and yield anomalies in the field with the same computer-vision engine.",
    to: "/industries/agriculture",
  },
  {
    Icon: Truck,
    title: "FOOD\nDISTRIBUTION",
    desc: "Verify freshness, grade produce at intake, and flag spoiled shipments before they hit the shelf.",
    to: "/industries/food-distribution",
  },
  {
    Icon: UtensilsCrossed,
    title: "RESTAURANTS &\nHOSPITALITY",
    desc: "Standardize plating, portion sizes, and ingredient quality across every location in a chain.",
    to: "/industries/restaurants",
  },
  {
    Icon: Boxes,
    title: "SUPPLY-CHAIN\nMONITORING",
    desc: "Trace batches from farm to shelf with automated visual audits at every hand-off point.",
    to: "/industries/supply-chain",
  },
];

export default function IndustriesSection() {
  return (
    <section className="px-6 pb-12" data-testid="industries-section">
      <div className="max-w-[1280px] mx-auto">
        <HUDFrame padding="p-7 md:p-10">
          {/* Heading */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-3">
            <div>
              <div className="hud-pill" data-testid="industries-pill">
                PLATFORM REACH
              </div>
              <h2
                className="mt-4 font-display font-black"
                style={{
                  fontSize: "clamp(24px, 3.4vw, 40px)",
                  letterSpacing: "0.05em",
                }}
              >
                <span style={{ color: "#ffffff" }}>FROM FARM TO SHELF TO PLATE.</span>
                <br />
                <span className="text-green glow">ONE AI. EVERY LINK OF THE CHAIN.</span>
              </h2>
            </div>
            <p
              className="text-[color:var(--ss-muted)] max-w-md md:text-right"
              style={{ fontSize: "14.5px", lineHeight: 1.55 }}
            >
              The same vision engine that identifies plant varieties and grades
              agricultural product adapts to any SKU that needs to be recognized,
              inspected, or tracked at scale.
            </p>
          </div>

          {/* Industries grid */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6">
            {INDUSTRIES.map((ind, i) => (
              <Link
                to={ind.to}
                key={ind.title}
                className="group flex flex-col items-center text-center px-2 hover:opacity-95 transition"
                data-testid={`industry-card-${i}`}
              >
                <div className="w-24 h-24 border border-[rgba(157,252,4,0.45)] grid place-items-center transition group-hover:border-[var(--ss-green)] group-hover:bg-[rgba(157,252,4,0.06)]">
                  <ind.Icon
                    size={54}
                    strokeWidth={1.4}
                    className="text-[color:var(--ss-green)] transition group-hover:drop-shadow-[0_0_10px_rgba(157,252,4,0.7)]"
                  />
                </div>
                <div
                  className="mt-4 font-display font-bold text-[color:var(--ss-green)] whitespace-pre-line leading-tight"
                  style={{ fontSize: "13px", letterSpacing: "0.18em" }}
                >
                  {ind.title}
                </div>
                <p
                  className="mt-3 text-[color:var(--ss-text)]/80"
                  style={{ fontSize: "12.5px", lineHeight: 1.45 }}
                >
                  {ind.desc}
                </p>
              </Link>
            ))}
          </div>

          {/* Bottom disclaimer strip */}
          <div className="mt-9 pt-5 border-t border-[rgba(157,252,4,0.18)] flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div
              className="font-display text-[color:var(--ss-green)]"
              style={{ fontSize: "12px", letterSpacing: "0.28em" }}
            >
              ONE PLATFORM · CONFIGURABLE FOR ANY VERTICAL
            </div>
            <div
              className="text-[color:var(--ss-muted)]"
              style={{ fontSize: "12.5px" }}
            >
              Interested in a custom deployment?{" "}
              <a
                href="/contact"
                className="text-[color:var(--ss-green)] hover:opacity-80 underline decoration-[rgba(157,252,4,0.35)] underline-offset-4"
                data-testid="industries-contact-link"
              >
                Talk to our team →
              </a>
            </div>
          </div>
        </HUDFrame>
      </div>
    </section>
  );
}
